import type {
  Question,
  PaginatedResponse,
  QuestionFilters,
  QuestionStats,
  FilterOptions,
} from '@/types';

// API base URL - configurable via environment variable for Cloudflare Pages deployment
// Falls back to relative path for GKE deployment or local development
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/questions';

let authToken: string | null = null;

/**
 * Set the auth token for API requests
 */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * Get the current auth token
 */
export function getAuthToken(): string | null {
  return authToken;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Build query string from filters
 */
function buildQueryString(
  filters: QuestionFilters,
  page: number,
  pageSize: number,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
): string {
  const params = new URLSearchParams();

  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  if (filters.status?.length) {
    filters.status.forEach((s) => params.append('status', s));
  }
  if (filters.source?.length) {
    filters.source.forEach((s) => params.append('source', s));
  }
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.search) params.set('search', filters.search);
  if (filters.category) params.set('category', filters.category);
  if (filters.groupName) params.set('groupName', filters.groupName);
  if (filters.answeredBy) params.set('answeredBy', filters.answeredBy);
  if (filters.tags?.length) {
    filters.tags.forEach((t) => params.append('tags', t));
  }
  if (filters.assignedDoctor) params.set('assignedDoctor', filters.assignedDoctor);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.isFavorite) params.set('isFavorite', 'true');

  return params.toString();
}

/**
 * Fetch paginated questions list
 */
export async function fetchQuestions(
  filters: QuestionFilters = {},
  page = 1,
  pageSize = 20,
  sortBy = 'timestamp',
  sortOrder: 'asc' | 'desc' = 'desc'
): Promise<PaginatedResponse<Question>> {
  const query = buildQueryString(filters, page, pageSize, sortBy, sortOrder);
  return apiRequest<PaginatedResponse<Question>>(`?${query}`);
}

/**
 * Fetch single question by ID
 */
export async function fetchQuestion(id: string): Promise<Question> {
  return apiRequest<Question>(`/${id}`);
}

/**
 * Fetch question statistics
 */
export async function fetchStats(
  filters: QuestionFilters = {}
): Promise<QuestionStats> {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.status?.length) {
    filters.status.forEach((s) => params.append('status', s));
  }
  if (filters.source?.length) {
    filters.source.forEach((s) => params.append('source', s));
  }
  if (filters.category) params.set('category', filters.category);
  if (filters.tags?.length) {
    filters.tags.forEach((t) => params.append('tags', t));
  }
  if (filters.answeredBy) params.set('answeredBy', filters.answeredBy);
  const query = params.toString();
  return apiRequest<QuestionStats>(`/stats${query ? `?${query}` : ''}`);
}

/**
 * Fetch filter options
 */
export async function fetchFilterOptions(): Promise<FilterOptions> {
  return apiRequest<FilterOptions>('/filters');
}
